/**
 * Pretty-printed JSON block with copy-to-clipboard. Operators paste
 * this into Slack for incident postmortems; the copy button removes
 * one click.
 */
import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

export function JsonBlock({
  value,
  className,
}: {
  value: unknown;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const text = JSON.stringify(value, null, 2);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  }

  return (
    <div className={cn("group relative", className)}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onCopy}
        className="absolute right-2 top-2 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
        aria-label="Copy JSON"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
      <pre
        data-testid="json-block"
        className="scrollbar-thin overflow-auto rounded-md border bg-muted/30 p-3 font-mono text-xs leading-relaxed"
      >
        {text}
      </pre>
    </div>
  );
}
